import type { UserAccessControlParams } from './itemAccessControl';

/**
 * Checks if a user can see a location based on its visibleTo group list.
 * If visibleTo is undefined/null, the location is visible to everyone in the organization.
 * If visibleTo is an empty array, the location is not visible to anyone (except admins).
 * If visibleTo contains group IDs, the location is visible to users in those groups.
 */
export function isLocationVisibleToUser(
  visibleTo: string[] | undefined | null,
  user: UserAccessControlParams
): boolean {
  const { userGroups, isSuperOrTenantAdmin, isInOrganizationGroup } = user;

  // Admins can always see all locations
  if (isSuperOrTenantAdmin) {
    return true;
  }

  // If visibleTo is not set, location is visible to everyone in the organization (legacy behavior)
  if (!visibleTo) {
    return isInOrganizationGroup;
  }

  // If visibleTo is an empty array, location is not visible to anyone except admins
  if (visibleTo.length === 0) {
    return false;
  }

  // Check if user is in any of the allowed groups
  return userGroups.some(userGroup => visibleTo.includes(userGroup));
}

/**
 * Checks if a user can update a location based on its updatableBy group list.
 * If updatableBy is undefined/null, the location is updatable by everyone in the organization.
 * If updatableBy is an empty array, the location is not updatable by anyone (except admins).
 * If updatableBy contains group IDs, the location is updatable by users in those groups.
 */
export function isLocationUpdatableByUser(
  updatableBy: string[] | undefined | null,
  user: UserAccessControlParams
): boolean {
  const { userGroups, isSuperOrTenantAdmin, isInOrganizationGroup } = user;

  // Admins can always update all locations
  if (isSuperOrTenantAdmin) {
    return true;
  }

  // If updatableBy is not set, location is updatable by everyone in the organization (legacy behavior)
  if (!updatableBy) {
    return isInOrganizationGroup;
  }

  // If updatableBy is an empty array, location is not updatable by anyone except admins
  if (updatableBy.length === 0) {
    return false;
  }

  // Check if user is in any of the allowed groups
  return userGroups.some(userGroup => updatableBy.includes(userGroup));
}
